import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet } from 'react-router';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container/index.tsx';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION_LEADERBOARD,
  ANY_SPORT_SERIES_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
  ANY_SPORT_SERIES_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp.ts';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { metadatas } from '@sorare/core/src/lib/seo/common';

import { CollectionHeader } from '#src/components/collections/CollectionHeader/index.tsx';
import { CollectionFilters } from '#src/components/myCards/CollectionFilters/index.tsx';
import { useGallerySearch } from '#src/hooks/my-cards/useGallerySearch.ts';

import type {
  CollectionLayoutQuery,
  CollectionLayoutQueryVariables,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(FullWidthContainer)`
  color: var(--c-white);
  min-height: var(--100vh);
`;

const COLLECTION_LAYOUT_QUERY = gql`
  query CollectionLayoutQuery($collection: String!, $slug: String!) {
    user(slug: $slug) {
      slug
      nickname
    }
    cardCollection(slug: $collection) {
      slug
      name
      cardSet {
        slug
      }
      userCardCollection(forUserSlug: $slug) {
        id
        slotsCount
        ...CollectionHeader_userCardCollection
      }
      socialPictureUrls(forUserSlug: $slug) {
        post
        square
        story
      }
      ...CollectionHeader_cardCollection
    }
  }
  ${CollectionHeader.fragments.cardCollection}
  ${CollectionHeader.fragments.userCardCollection}
` as TypedDocumentNode<CollectionLayoutQuery, CollectionLayoutQueryVariables>;

type Props = {
  userSlug: string;
  collection: string;
  fromMyClub?: boolean;
};
export const CollectionLayout = ({
  userSlug,
  collection,
  fromMyClub,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const { isMe } = useCurrentUserContext();
  const isSeries = useSeriesNav();
  const { formatMessage } = useIntl();
  const { isIosApp } = useIsMobileApp();
  const { data } = useQuery(COLLECTION_LAYOUT_QUERY, {
    variables: {
      collection,
      slug: userSlug,
    },
  });
  const { user, cardCollection } = data || {};
  const userCardCollection = cardCollection?.userCardCollection;

  const {
    search,
    skin,
    showAll,
    onSearchChange,
    onSkinChange,
    onShowAllChange,
  } = useGallerySearch();

  const canSetMetadata = !!user?.nickname && !!cardCollection?.name;

  useTitleAndDescription(
    metadatas.collection.title,
    undefined,
    canSetMetadata
      ? { username: user?.nickname, collectionName: cardCollection.name }
      : false,
    {
      img:
        cardCollection?.socialPictureUrls?.post ||
        cardCollection?.socialPictureUrls?.square ||
        cardCollection?.socialPictureUrls?.story ||
        undefined,
    }
  );

  const cardsPath = isMe(userSlug)
    ? generateSportContextPath(
        isSeries
          ? ANY_SPORT_SERIES_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS
          : ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_CARDS,
        {
          params: {
            collectionSlug: collection,
          },
        }
      )
    : generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION, {
        params: {
          collection,
          slug: userSlug,
        },
      });

  const leaderboardPath = isMe(userSlug)
    ? generateSportContextPath(
        isSeries
          ? ANY_SPORT_SERIES_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD
          : ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG_LEADERBOARD,
        {
          params: {
            collectionSlug: collection,
          },
        }
      )
    : generateSportContextPath(
        ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION_LEADERBOARD,
        {
          params: {
            collection,
            slug: userSlug,
          },
        }
      );

  const { team } = cardCollection || {};

  return (
    <>
      <NoIndex />
      {!fromMyClub && !isIosApp && <PageHeader text={team?.name} />}
      <CollectionHeader
        userCardCollectionSlug={userSlug}
        userCardCollection={userCardCollection}
        cardCollection={cardCollection}
      >
        <Container maxWidth={600}>
          {isMe(userSlug) && !!cardCollection?.cardSet && (
            <CollectionFilters
              search={search}
              onSearchChange={onSearchChange}
              skin={skin}
              onSkinChange={onSkinChange}
              setSlug={cardCollection.cardSet.slug}
              showAll={cardCollection.rarity === Rarity.common ? showAll : true}
              onShowAllChange={onShowAllChange}
              placeholder={formatMessage(
                {
                  defaultMessage: 'Search {count} cards',
                },
                { count: userCardCollection?.slotsCount ?? 0 }
              )}
            />
          )}
        </Container>
      </CollectionHeader>
      <TabBar fullWidth variant="flat" transparent>
        <Tab to={cardsPath} matches={[cardsPath]} replace>
          <FormattedMessage {...glossary.cards} />
        </Tab>
        <Tab to={leaderboardPath} matches={[leaderboardPath]} replace>
          <FormattedMessage {...fantasy.leaderboard} />
        </Tab>
      </TabBar>
      <Wrapper>
        <Outlet />
      </Wrapper>
    </>
  );
};
